import { Box3, BufferGeometry, Group, Line, Vector3, type Object3D } from 'three'
import BaseFileLoader from './base-file-loader'
import { ThreeItemKeys, ThreeOriginalFileTypes } from '../../../constants/three'
import { setFileLoadingStatus } from '../../../stores/file-loading'
import { DXFLoader } from 'three-dxf-loader'

// Maximum file size for full DXF processing (30MB)
// Larger files skip some optimization passes to prevent memory issues
const MAX_FULL_PROCESSING_SIZE_BYTES = 30 * 1024 * 1024

/**
 * DXFFileLoader - Loads and processes DXF files for 3D printing
 *
 * Main responsibilities:
 * - Load DXF files and extract entities (lines, polylines, circles, arcs, etc.)
 * - Convert entities to Three.js objects (Line, Mesh, etc.)
 * - Apply appropriate materials
 * - Handle units and scaling
 * - Return optimized Groups containing all loaded objects
 *
 * Optimization features:
 * - Simplify lines by removing colinear points
 * - Filter out lines that are too short (< 0.1 units)
 * - Group connected lines together
 * - Group lines with identical XY projection but different Z values
 * - Detect 2D vs 3D based on Z coordinate variance
 * - Remove CAD software grouping and create clean structure with sub-groups for physically separated elements
 *
 * Technical constraints:
 * - DXFLoader return structure is not precisely defined
 * - Main objects of interest are Lines
 * - Lines can be nested in multiple groups with various transformations
 * - All parent transformations must be applied to Lines to get correct world coordinates for simplification
 *
 * Implementation approach:
 * 1. Flatten all Lines from nested groups and apply world transformations
 * 2. Simplify each line by removing colinear points
 * 3. Filter out lines that are too short
 * 4. Group connected lines (sharing endpoints)
 * 5. Group lines with same XY projection but different Z values
 */

class DXFFileLoader extends BaseFileLoader {
  protected getWorkerFileType(): FileParserSupportedType {
    return 'dxf'
  }

  async load(): Promise<Group> {
    return this.loadWithProgress(async () => {
      if (!this.arrayBuffer) {
        await this.loadArrayBuffer()
      }

      let entities: Object3D[]

      if (this.shouldUseWorker()) {
        const parsedObjects = await this.loadWithWorker()
        entities = this.processParsedObjects(parsedObjects)
      }
      else {
        entities = await this.processOnMainThread()
      }

      return this.addMeshesToScene(entities, this.file, (mesh, isGroup) => isGroup
        ? ({})
        : ({
            [ThreeItemKeys.LAYERS_AMOUNT]: 1,
            [ThreeItemKeys.ORIGINAL_FILE_TYPE]: this.isDxf2D(mesh) ? ThreeOriginalFileTypes.DXF_2D : ThreeOriginalFileTypes.DXF_3D,
          }))
    })
  }

  /**
   * Process objects parsed by the worker
   */
  private processParsedObjects(parsedObjects: Object3D[]): Object3D[] {
    return parsedObjects.map((entity) => {
      entity.traverse((child) => {
        if ('material' in child) {
          child.material = this.getLineMaterial()
        }
      })
      return this.scaleDownObject(entity)
    })
  }

  /**
   * Process DXF on main thread for smaller files
   */
  private processOnMainThread(): Object3D[] {
    const loader = new DXFLoader()
    loader.setConsumeUnits(true)

    setFileLoadingStatus('parsing', this.file.name, 20)

    // Convert ArrayBuffer to text for DXF parsing
    const text = new TextDecoder().decode(this.arrayBuffer!)
    const fileSize = this.arrayBuffer!.byteLength

    // Release the ArrayBuffer early to free memory
    this.arrayBuffer = undefined

    // Create a data URL from the text content for the loader
    const dataUrl = `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`

    return new Promise<Object3D[]>((resolve) => {
      loader.load(dataUrl, (item: { entity: Group }) => {
        setFileLoadingStatus('processing', this.file.name, 30)

        // Update world matrices BEFORE cloning to preserve transformations
        item.entity.updateMatrixWorld(true)

        // Clone to avoid modifying the original during processing
        const clonedEntity = item.entity.clone()

        // Important: After cloning, we need to update matrices again on the clone
        // because clone() doesn't copy the matrixWorld
        clonedEntity.updateMatrixWorld(true)

        // For large files, use simplified processing to prevent memory issues
        const useSimplifiedProcessing = fileSize > MAX_FULL_PROCESSING_SIZE_BYTES

        setFileLoadingStatus('processing', this.file.name, 40)

        const separatedEntities = this.simplifyDxf(clonedEntity, useSimplifiedProcessing).map(
          (entity) => {
            entity.traverse((child) => {
              if ('material' in child) {
                child.material = this.getLineMaterial()
              }
            })

            // Auto-scale based on bounding box size
            return this.scaleDownObject(entity)
          })

        setFileLoadingStatus('finalizing', this.file.name, 80)
        resolve(separatedEntities)
      })
    }) as unknown as Object3D[]
  }

  private simplifyDxf(group: Group, useSimplifiedProcessing = false): Group[] {
    // Step 1: Flatten all lines and apply transformations
    const allLines = this.flattenAndTransformLines(group)

    if (allLines.length === 0) {
      return []
    }

    // For large files, skip expensive grouping operations
    if (useSimplifiedProcessing) {
      // Just simplify lines and return as a single group
      const simplifiedLines = allLines.map(line => this.simplifyLine(line))
      const singleGroup = new Group()
      simplifiedLines.forEach(line => singleGroup.add(line))
      return [singleGroup]
    }

    // Step 2: Simplify each line (remove colinear points)
    // Note: coordinates are still in mm at this point; for reference, 100 mm corresponds to 0.1 m after scaling
    const simplifiedLines = allLines
      .map(line => this.simplifyLine(line))

    // Step 3: Group lines that are connected (share endpoints)
    const connectedGroups = this.groupConnectedLines(simplifiedLines)

    // Step 4: Group by same XY projection but different Z (stack layers)
    const xyMergedGroups = this.groupLinesByXY(connectedGroups)

    // Step 5: Group by spatial proximity to separate physical entities
    const finalGroups = this.groupGroupsBySpatialProximity(xyMergedGroups)

    return finalGroups
  }

  /**
   * Flatten all Lines from the group hierarchy and apply all parent transformations
   */
  private flattenAndTransformLines(group: Group): Line[] {
    const lines: Line[] = []

    // Matrices should already be updated before calling this method
    group.traverse((child) => {
      if (child.type === 'Line') {
        const line = child as Line
        const transformedLine = this.applyWorldTransform(line)
        lines.push(transformedLine)
      }
    })

    return lines
  }

  /**
   * Apply world transformation matrix to a line
   */
  private applyWorldTransform(line: Line): Line {
    // World matrices should already be updated on the parent group
    const positions = line.geometry.attributes.position.array
    const worldPoints: Vector3[] = []

    // Apply world matrix to all points
    for (let i = 0; i < positions.length; i += 3) {
      const point = new Vector3(
        positions[i],
        positions[i + 1],
        positions[i + 2],
      )

      // Apply the world matrix to get actual coordinates
      point.applyMatrix4(line.matrixWorld)

      worldPoints.push(point)
    }

    const newLine = new Line(
      new BufferGeometry().setFromPoints(worldPoints),
      line.material,
    )

    return newLine
  }

  /**
   * Simplify a line using Ramer-Douglas-Peucker algorithm
   * This preserves curves while removing redundant points on straight segments
   * Tolerance of 0.001 = 1mm in original scale, preserves rounded shapes
   */
  private simplifyLine(line: Line, tolerance = 0.001): Line {
    const positions = line.geometry.attributes.position.array
    const points: Vector3[] = []

    for (let i = 0; i < positions.length; i += 3) {
      points.push(new Vector3(
        positions[i],
        positions[i + 1],
        positions[i + 2],
      ))
    }

    if (points.length < 3) {
      return line
    }

    const simplified = this.ramerDouglasPeucker(points, tolerance)

    const newLine = new Line(
      new BufferGeometry().setFromPoints(simplified),
      line.material,
    )

    return newLine
  }

  /**
   * Ramer-Douglas-Peucker algorithm for polyline simplification
   * Preserves shape while reducing point count
   */
  private ramerDouglasPeucker(points: Vector3[], tolerance: number): Vector3[] {
    if (points.length <= 2) {
      return points
    }

    // Find the point with maximum distance from the line segment
    let maxDistance = 0
    let maxIndex = 0
    const start = points[0]
    const end = points.at(-1)!

    for (let i = 1; i < points.length - 1; i++) {
      const distance = this.perpendicularDistance(points[i], start, end)
      if (distance > maxDistance) {
        maxDistance = distance
        maxIndex = i
      }
    }

    // If max distance is greater than tolerance, recursively simplify
    if (maxDistance > tolerance) {
      // Recursive call on both sides
      const left = this.ramerDouglasPeucker(points.slice(0, maxIndex + 1), tolerance)
      const right = this.ramerDouglasPeucker(points.slice(maxIndex), tolerance)

      // Combine results (remove duplicate middle point)
      return [...left.slice(0, -1), ...right]
    }
    else {
      // All points are within tolerance, keep only endpoints
      return [start, end]
    }
  }

  /**
   * Calculate perpendicular distance from a point to a line segment
   */
  private perpendicularDistance(point: Vector3, lineStart: Vector3, lineEnd: Vector3): number {
    const line = new Vector3().subVectors(lineEnd, lineStart)
    const lineLength = line.length()

    if (lineLength === 0) {
      return point.distanceTo(lineStart)
    }

    // Calculate projection of point onto line
    const pointVec = new Vector3().subVectors(point, lineStart)
    const t = Math.max(0, Math.min(1, pointVec.dot(line) / (lineLength * lineLength)))

    // Find closest point on line segment
    const projection = new Vector3().copy(lineStart).add(line.multiplyScalar(t))

    return point.distanceTo(projection)
  }

  /**
   * Group already-formed groups by spatial proximity
   * Groups that are far apart should stay separate
   * After 0.001 scaling, threshold = 3 units (original 3000mm = 3m)
   */
  private groupGroupsBySpatialProximity(groups: Group[], proximityThreshold = 3): Group[] {
    if (groups.length === 0) return []

    const finalGroups: Group[] = []
    const used = new Set<number>()

    for (let i = 0; i < groups.length; i++) {
      if (used.has(i)) continue

      const toMerge = this.findProximateGroups(i, groups, used, proximityThreshold)
      const mergedGroup = this.mergeGroupsByIndices(groups, toMerge)

      if (mergedGroup.children.length > 0) {
        finalGroups.push(mergedGroup)
      }
    }

    return finalGroups
  }

  /**
   * Find all groups within proximity threshold starting from a seed group
   */
  private findProximateGroups(
    seedIndex: number,
    groups: Group[],
    used: Set<number>,
    proximityThreshold: number,
  ): number[] {
    const toMerge: number[] = [seedIndex]
    used.add(seedIndex)

    let addedInLastPass = true
    while (addedInLastPass) {
      addedInLastPass = false

      for (let j = 0; j < groups.length; j++) {
        if (used.has(j)) continue

        const isClose = toMerge.some(idx =>
          this.areGroupsNear(groups[idx], groups[j], proximityThreshold),
        )

        if (isClose) {
          toMerge.push(j)
          used.add(j)
          addedInLastPass = true
        }
      }
    }

    return toMerge
  }

  /**
   * Merge multiple groups by their indices into a single group
   */
  private mergeGroupsByIndices(groups: Group[], indices: number[]): Group {
    const mergedGroup = new Group()
    indices.forEach((idx) => {
      groups[idx].children.forEach(child => mergedGroup.add(child.clone()))
    })
    return mergedGroup
  }

  /**
   * Check if two groups are spatially near each other (based on XY distance only, ignoring Z)
   */
  private areGroupsNear(group1: Group, group2: Group, threshold: number): boolean {
    const bbox1 = new Box3().setFromObject(group1)
    const bbox2 = new Box3().setFromObject(group2)

    const center1 = new Vector3()
    const center2 = new Vector3()
    bbox1.getCenter(center1)
    bbox2.getCenter(center2)

    // Use only XY distance to separate cells, ignoring Z (layers)
    const xyDistance = Math.sqrt(
      Math.pow(center1.x - center2.x, 2)
      + Math.pow(center1.y - center2.y, 2),
    )

    return xyDistance < threshold
  }

  /**
   * Group lines that are connected (share endpoints)
   * Uses spatial hashing
   */
  private groupConnectedLines(lines: Line[]): Group[] {
    if (lines.length === 0) return []

    const groups: Group[] = []

    // Use Union-Find for efficient grouping
    const parent = new Int32Array(lines.length)
    for (let i = 0; i < lines.length; i++) {
      parent[i] = i
    }

    const find = (x: number): number => {
      if (parent[x] !== x) {
        parent[x] = find(parent[x])
      }
      return parent[x]
    }

    const union = (x: number, y: number): void => {
      const px = find(x)
      const py = find(y)
      if (px !== py) {
        parent[px] = py
      }
    }

    // Build spatial hash for endpoints
    const epsilon = 0.1
    const cellSize = epsilon * 2
    const endpointToLineIndex = new Map<string, number[]>()

    const hashPoint = (p: Vector3): string => {
      const x = Math.floor(p.x / cellSize)
      const y = Math.floor(p.y / cellSize)
      const z = Math.floor(p.z / cellSize)
      return `${x},${y},${z}`
    }

    // Get neighboring cell keys for a point
    const getNeighborKeys = (p: Vector3): string[] => {
      const x = Math.floor(p.x / cellSize)
      const y = Math.floor(p.y / cellSize)
      const z = Math.floor(p.z / cellSize)
      const keys: string[] = []
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          for (let dz = -1; dz <= 1; dz++) {
            keys.push(`${x + dx},${y + dy},${z + dz}`)
          }
        }
      }
      return keys
    }

    // Index all endpoints
    for (let i = 0; i < lines.length; i++) {
      const endpoints = this.getLineEndpoints(lines[i])
      for (const ep of endpoints) {
        const key = hashPoint(ep)
        if (!endpointToLineIndex.has(key)) {
          endpointToLineIndex.set(key, [])
        }
        endpointToLineIndex.get(key)!.push(i)
      }
    }

    // Find connected lines using spatial hash
    for (let i = 0; i < lines.length; i++) {
      const endpoints = this.getLineEndpoints(lines[i])

      for (const ep of endpoints) {
        const neighborKeys = getNeighborKeys(ep)

        for (const key of neighborKeys) {
          const candidates = endpointToLineIndex.get(key)
          if (!candidates) continue

          for (const j of candidates) {
            if (i >= j) continue // Avoid duplicate checks

            if (this.areLinesConnected(lines[i], lines[j], epsilon)) {
              union(i, j)
            }
          }
        }
      }
    }

    // Clear the spatial hash to free memory
    endpointToLineIndex.clear()

    // Group lines by their root
    const groupMap = new Map<number, Line[]>()
    for (let i = 0; i < lines.length; i++) {
      const root = find(i)
      if (!groupMap.has(root)) {
        groupMap.set(root, [])
      }
      groupMap.get(root)!.push(lines[i])
    }

    // Create Group objects
    for (const lineGroup of groupMap.values()) {
      const group = new Group()

      lineGroup.forEach(line => group.add(line.clone()))
      groups.push(group)
    }

    return groups
  }

  /**
   * Check if two lines are connected (share an endpoint)
   */
  private areLinesConnected(line1: Line, line2: Line, epsilon = 0.1): boolean {
    const endpoints1 = this.getLineEndpoints(line1)
    const endpoints2 = this.getLineEndpoints(line2)

    for (const ep1 of endpoints1) {
      for (const ep2 of endpoints2) {
        if (ep1.distanceTo(ep2) < epsilon) {
          return true
        }
      }
    }

    return false
  }

  /**
   * Get the start and end points of a line
   */
  private getLineEndpoints(line: Line): Vector3[] {
    const positions = line.geometry.attributes.position.array
    const start = new Vector3(positions[0], positions[1], positions[2])
    const end = new Vector3(
      positions[positions.length - 3],
      positions[positions.length - 2],
      positions[positions.length - 1],
    )
    return [start, end]
  }

  /**
   * Group lines that have the same XY projection but different Z values
   */
  private groupLinesByXY(groups: Group[]): Group[] {
    const xyGroups: Map<string, Group> = new Map()

    for (const group of groups) {
      const xyKey = this.getGroupXYKey(group)

      if (xyGroups.has(xyKey)) {
        // Add to existing group
        const existingGroup = xyGroups.get(xyKey)!
        group.children.forEach(child => existingGroup.add(child.clone()))
      }
      else {
        // Create new group
        xyGroups.set(xyKey, group.clone())
      }
    }

    return Array.from(xyGroups.values())
  }

  /**
   * Generate a key for XY grouping based on bounding box
   */
  private getGroupXYKey(group: Group, precision = 100): string {
    const bbox = new Box3().setFromObject(group)

    // Round to precision to handle floating point errors
    const minX = Math.round(bbox.min.x * precision) / precision
    const minY = Math.round(bbox.min.y * precision) / precision
    const maxX = Math.round(bbox.max.x * precision) / precision
    const maxY = Math.round(bbox.max.y * precision) / precision

    return `${minX},${minY},${maxX},${maxY}`
  }

  private isDxf2D(object: Object3D) {
    const boundingBox = new Box3().setFromObject(object)

    return Math.abs(boundingBox.min.z - boundingBox.max.z) <= 0.2
  }
}

export default DXFFileLoader
